import { atom } from 'nanostores';
import type { ITerminal } from '~/types/terminal';
import { coloredText } from '~/utils/terminal';
import { chatStore } from '~/lib/stores/chat';
import type { FileMap } from './files';
import {
  BrowserMetroRunner,
  type RunnerError,
  type RunnerLogEntry,
} from '~/lib/reactNativeRunner/browserMetroRunner.client';
import { toBrowserMetroFiles, type BrowserMetroFiles } from '~/lib/reactNativeRunner/fileMap';
import type { ContentChange } from '~/lib/reactNativeRunner/protocol';
import { ReactNativePreviewReporter } from '~/lib/reactNativeRunner/monitoring';

export type RunnerErrorSink = (error: RunnerError) => void;

const FLUSH_DEBOUNCE_MS = 400;
const INITIAL_BUILD_MAX_ATTEMPTS = 3;
const INITIAL_BUILD_RETRY_DELAYS_MS = [1_000, 3_000];

export function isRetryableInitialBuildError(error: RunnerError): boolean {
  const httpStatus = error.message.match(/HTTP (\d{3})/i);

  if (httpStatus) {
    const status = Number(httpStatus[1]);
    return status === 408 || status === 429 || status >= 500;
  }

  return /timed out|failed to fetch|fetch failed|network(?:error| request failed)|package server unavailable/i.test(
    error.message,
  );
}

/**
 * Store analogous to `PreviewsStore`, but backed by the browser-metro runner
 * instead of nodepod. For `framework === 'reactnative'` projects the workbench
 * activates this store instead of mounting/running nodepod:
 *
 *   - `setActive(true)` turns the runner on; `setFiles(files)` feeds it the
 *     workbench's file map, starting once a `package.json` exists and AI/file
 *     streaming has settled, then pushing debounced HMR updates thereafter
 *   - `previewUrl` / `ready` / `bundling` drive the RN preview surface
 *   - build + runtime logs stream to the read-only terminal (`attachTerminal`)
 *   - bundler + runtime errors are routed to the registered error sink (wired to
 *     `workbenchStore.previewError`) so the existing error-fix loop keeps working
 */
export class ReactNativeRunnerStore {
  previewUrl = atom<string | undefined>(undefined);
  ready = atom<boolean>(false);
  bundling = atom<boolean>(false);

  #runner: BrowserMetroRunner | null = null;
  #active = false;

  /** browser-metro path -> content, of what the worker currently has. */
  #snapshot = new Map<string, string>();
  #pending = new Map<string, ContentChange>();
  #flushTimer: ReturnType<typeof setTimeout> | null = null;
  #initialBuildRetryTimer: ReturnType<typeof setTimeout> | null = null;
  #initialBuildAttempt = 0;
  #initialRestoreInProgress = false;
  #latestFiles: FileMap | undefined;

  #terminal: ITerminal | undefined;
  #logBuffer: string[] = [];
  #errorSink: RunnerErrorSink | undefined;
  #unsubStreaming: (() => void) | undefined;

  /** Bundler error raised while the AI was streaming, held for re-emission. */
  #deferredError: RunnerError | undefined;
  #reporter = new ReactNativePreviewReporter();

  // --- Activation -------------------------------------------------------------

  setErrorSink(sink: RunnerErrorSink): void {
    this.#errorSink = sink;
  }

  /** Enable/disable the runner. Called by the workbench on framework changes. */
  setActive(active: boolean): void {
    if (active === this.#active) {
      return;
    }

    this.#active = active;

    console.info('[rn-runner] setActive:', active);

    if (!active) {
      this.stop();
    } else {
      this.#subscribeToStreaming();
    }
  }

  get active(): boolean {
    return this.#active;
  }

  get initialRestoreInProgress(): boolean {
    return this.#initialRestoreInProgress;
  }

  /**
   * Hold runner startup while persisted files are replayed after a refresh.
   * The restore can begin before the saved framework switches to React Native,
   * so this gate is deliberately independent of the active state.
   */
  beginInitialRestore(): void {
    if (this.#initialRestoreInProgress) {
      return;
    }

    this.#initialRestoreInProgress = true;
    console.info('[rn-runner] initial file restore started');
  }

  /** Release the restore gate and cold-start once from the final file map. */
  endInitialRestore(): void {
    if (!this.#initialRestoreInProgress) {
      return;
    }

    this.#initialRestoreInProgress = false;
    console.info('[rn-runner] initial file restore complete');

    if (this.#active && this.#latestFiles) {
      this.setFiles(this.#latestFiles);
    }
  }

  // --- File sync --------------------------------------------------------------

  /** Feed the current workbench file map. No-op unless the runner is active. */
  setFiles(files: FileMap): void {
    console.log('[native] setFiles function called');

    if (!this.#active || typeof window === 'undefined') {
      return;
    }

    this.#latestFiles = files;

    if (this.#initialRestoreInProgress) {
      console.info('[rn-runner] setFiles: waiting for initial file restore');
      return;
    }

    const bmFiles = toBrowserMetroFiles(files);

    if (!this.#runner || !this.#runner.started) {
      // Wait until there is a package.json so the worker can resolve an entry.
      if (!bmFiles['/package.json']) {
        console.info('[rn-runner] setFiles: waiting for package.json (' + Object.keys(bmFiles).length + ' files)');
        return;
      }

      // package.json is usually the first generated file. Starting here while
      // the rest of the project is still streaming creates a slow empty build
      // followed by an update racing the same dependency graph.
      if (chatStore.get().isStreaming) {
        console.info('[rn-runner] setFiles: waiting for AI streaming to finish');
        return;
      }

      if (this.#initialBuildRetryTimer) {
        return;
      }

      console.info('[rn-runner] setFiles: starting runner (' + Object.keys(bmFiles).length + ' files)');
      this.#startRunner(bmFiles);
      return;
    }

    this.#accumulateChanges(bmFiles);

    // `started` means the watch request was posted, not that the initial graph
    // is ready. Keep changes buffered until watch-ready to avoid build races.
    if (this.ready.get()) {
      this.#scheduleFlush();
    }
  }

  /**
   * Cold-restart the bundler — the RN counterpart of the preview header's
   * "Restart" button killing and relaunching the dev server.
   *
   * There is no shell to restart here, so this tears the worker down (dropping
   * its module cache, npm cache and VirtualFS) and rebuilds the current file map
   * from scratch. That is what makes it worth pressing: a plain reload would
   * reuse the same caches, while this refetches from the ESM server and rebuilds
   * every module.
   *
   * No-op before the first build (there is nothing to restart yet) — `setFiles`
   * starts the runner as soon as a `package.json` shows up.
   */
  restart(): void {
    if (!this.#active || typeof window === 'undefined' || !this.#runner) {
      return;
    }

    const latestFiles = this.#latestFiles;

    this.#writeLog({ level: 'info', text: 'Restarting bundler…' });
    this.stop();

    if (!latestFiles) {
      return;
    }

    this.#subscribeToStreaming();
    this.setFiles(latestFiles);
  }

  #startRunner(bmFiles: BrowserMetroFiles, resetAttempts = true): void {
    if (resetAttempts) {
      this.#initialBuildAttempt = 0;
    }

    this.#initialBuildAttempt++;

    if (!this.#runner) {
      this.#runner = new BrowserMetroRunner({
        onPreviewUrl: (url) => this.previewUrl.set(url),
        onReady: () => {
          this.ready.set(true);
          this.#initialBuildAttempt = 0;

          if (this.#pending.size > 0) {
            this.#flush();
          }
        },
        onBuildSuccess: () => {
          // The project compiles again — whatever failed mid-stream is moot.
          this.#deferredError = undefined;
          this.#reporter.markBuildSuccess();
        },
        onBundling: (bundling) => this.bundling.set(bundling),
        onLog: (entry) => this.#writeLog(entry),
        onInitialBuildError: (error) => this.#handleInitialBuildError(error),
        onError: (error) => this.#emitError(error),
      });
    }

    this.ready.set(false);
    this.#snapshot = new Map(Object.entries(bmFiles).map(([p, e]) => [p, e.content]));
    this.#pending.clear();
    this.#runner.start(bmFiles);
  }

  #subscribeToStreaming(): void {
    if (this.#unsubStreaming) {
      return;
    }

    this.#unsubStreaming = chatStore.subscribe((state) => {
      if (state.isStreaming) {
        return;
      }

      if (
        this.#active &&
        !this.#initialRestoreInProgress &&
        this.#latestFiles &&
        (!this.#runner || !this.#runner.started) &&
        !this.#initialBuildRetryTimer
      ) {
        this.setFiles(this.#latestFiles);
        return;
      }

      if (this.#pending.size > 0) {
        // The rebuild these changes trigger decides the error state itself —
        // it either succeeds (clearing the held error) or reports a fresh one.
        this.#flush();
        return;
      }

      this.#flushDeferredError();
    });
  }

  #handleInitialBuildError(error: RunnerError): void {
    this.ready.set(false);

    if (
      !this.#active ||
      !isRetryableInitialBuildError(error) ||
      this.#initialBuildAttempt >= INITIAL_BUILD_MAX_ATTEMPTS
    ) {
      this.#emitError(error);
      return;
    }

    const delay = INITIAL_BUILD_RETRY_DELAYS_MS[this.#initialBuildAttempt - 1] ?? 3_000;
    this.#writeLog({
      level: 'warn',
      text: `Initial build failed; retrying in ${delay / 1000}s (attempt ${this.#initialBuildAttempt + 1}/${INITIAL_BUILD_MAX_ATTEMPTS})`,
    });

    this.#initialBuildRetryTimer = setTimeout(() => {
      this.#initialBuildRetryTimer = null;

      if (!this.#active || !this.#latestFiles || chatStore.get().isStreaming || this.#initialRestoreInProgress) {
        return;
      }

      const bmFiles = toBrowserMetroFiles(this.#latestFiles);

      if (!bmFiles['/package.json']) {
        return;
      }

      this.#startRunner(bmFiles, false);
    }, delay);
  }

  #accumulateChanges(bmFiles: BrowserMetroFiles): void {
    // Additions / updates. A path that is still pending as a 'create' stays a
    // 'create' even if it changes again before the batch flushes, so the worker
    // still registers it as a new expo-router route (route detection keys on
    // create/delete, not update).
    for (const [path, entry] of Object.entries(bmFiles)) {
      const prev = this.#snapshot.get(path);
      const pending = this.#pending.get(path);

      if (prev === undefined) {
        this.#pending.set(path, { path, type: 'create', content: entry.content });
      } else if (prev !== entry.content) {
        this.#pending.set(path, {
          path,
          type: pending?.type === 'create' ? 'create' : 'update',
          content: entry.content,
        });
      }
    }

    // Deletions.
    for (const path of this.#snapshot.keys()) {
      if (!(path in bmFiles)) {
        this.#pending.set(path, { path, type: 'delete' });
      }
    }

    // Update the snapshot to the new desired state.
    this.#snapshot = new Map(Object.entries(bmFiles).map(([p, e]) => [p, e.content]));
  }

  #scheduleFlush(): void {
    if (this.#flushTimer) {
      clearTimeout(this.#flushTimer);
    }

    this.#flushTimer = setTimeout(() => this.#flush(), FLUSH_DEBOUNCE_MS);
  }

  #flush(): void {
    if (this.#flushTimer) {
      clearTimeout(this.#flushTimer);
      this.#flushTimer = null;
    }

    // Hold updates until streaming settles — avoids bundling half-written files.
    if (chatStore.get().isStreaming) {
      return;
    }

    if (!this.#runner || !this.ready.get() || this.#pending.size === 0) {
      return;
    }

    const changes = [...this.#pending.values()];
    this.#pending.clear();
    this.#runner.update(changes);
  }

  // --- Preview iframe ---------------------------------------------------------

  attachIframe(iframe: HTMLIFrameElement | null): void {
    this.#runner?.attachIframe(iframe);
  }

  // --- Terminal / logs --------------------------------------------------------

  /** Bind the read-only terminal to the runner's log stream (replays history). */
  attachTerminal(terminal: ITerminal): void {
    this.#terminal = terminal;

    for (const line of this.#logBuffer) {
      terminal.write(line);
    }
  }

  #writeLog(entry: RunnerLogEntry): void {
    const prefix =
      entry.level === 'error' ? coloredText.red('error') : entry.level === 'warn' ? coloredText.red('warn') : 'info';
    const line = `\x1b[90m[metro]\x1b[0m ${prefix}: ${entry.text}\r\n`;

    // Keep a bounded backlog so a late-attaching terminal still shows history.
    this.#logBuffer.push(line);

    if (this.#logBuffer.length > 500) {
      this.#logBuffer.shift();
    }

    this.#terminal?.write(line);
  }

  #emitError(error: RunnerError): void {
    /*
     * Mirror PreviewsStore: don't report errors while the AI is streaming — a
     * half-written file fails to bundle and fixes itself moments later. But the
     * initial build starts mid-stream (as soon as a package.json lands), so a
     * genuine failure there — an ESM-server fetch that 500s, an unresolvable
     * import — used to be logged and then dropped, leaving a dead preview the
     * error-fix loop never heard about. Hold it instead and settle it once
     * streaming ends: a later successful build clears it, otherwise it is
     * emitted for real.
     */
    if (chatStore.get().isStreaming) {
      this.#deferredError = error;
      return;
    }

    this.#deferredError = undefined;
    this.#reporter.capture(error);
    this.#errorSink?.(error);
  }

  /** Emit the error held during streaming, if nothing has since fixed it. */
  #flushDeferredError(): void {
    const error = this.#deferredError;

    if (!error || chatStore.get().isStreaming) {
      return;
    }

    this.#deferredError = undefined;
    this.#reporter.capture(error);
    this.#errorSink?.(error);
  }

  // --- Lifecycle --------------------------------------------------------------

  stop(): void {
    if (this.#flushTimer) {
      clearTimeout(this.#flushTimer);
      this.#flushTimer = null;
    }

    if (this.#initialBuildRetryTimer) {
      clearTimeout(this.#initialBuildRetryTimer);
      this.#initialBuildRetryTimer = null;
    }

    this.#unsubStreaming?.();
    this.#unsubStreaming = undefined;
    this.#pending.clear();
    this.#snapshot.clear();
    this.#latestFiles = undefined;
    this.#initialBuildAttempt = 0;
    this.#deferredError = undefined;
    this.#reporter.reset();

    this.#runner?.dispose();
    this.#runner = null;

    this.previewUrl.set(undefined);
    this.ready.set(false);
    this.bundling.set(false);
  }
}

export const reactNativeRunnerStore = new ReactNativeRunnerStore();
